import {DEFAULT_PALETTE} from './defaultPalette';
import {TMergeValues, TPaletteColor} from './definitions';

export type TBrandingPalette<
  TCustomPalette extends Readonly<Record<string, unknown>> = TEmptyObject,
> = {
  colors: Record<
    'disabled' | 'accentA' | 'accentB' | 'error' | 'warning' | 'success' | 'info',
    [keyof TCustomPalette] extends [never]
      ? (typeof DEFAULT_PALETTE)[keyof typeof DEFAULT_PALETTE]
      : TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>[
          | keyof typeof DEFAULT_PALETTE
          | keyof TCustomPalette]
  >;
  backgrounds: Record<
    'primary' | 'secondary' | 'error' | 'accent',
    [keyof TCustomPalette] extends [never]
      ? (typeof DEFAULT_PALETTE)[keyof typeof DEFAULT_PALETTE]
      : TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>[
          | keyof typeof DEFAULT_PALETTE
          | keyof TCustomPalette]
  >;
  content: Record<
    'primary' | 'secondary' | 'tertiary',
    [keyof TCustomPalette] extends [never]
      ? (typeof DEFAULT_PALETTE)[keyof typeof DEFAULT_PALETTE]
      : TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>[
          | keyof typeof DEFAULT_PALETTE
          | keyof TCustomPalette]
  >;
};

export const DEFAULT_BRANDING = {
  colors: {
    disabled: DEFAULT_PALETTE.grey400,
    accentA: DEFAULT_PALETTE.brandAccent400,
    accentB: DEFAULT_PALETTE.pink400,
    error: DEFAULT_PALETTE.red500,
    warning: DEFAULT_PALETTE.yellow500,
    success: DEFAULT_PALETTE.green500,
    info: DEFAULT_PALETTE.blue500,
  },
  backgrounds: {
    primary: DEFAULT_PALETTE.white,
    secondary: DEFAULT_PALETTE.grey50,
    error: DEFAULT_PALETTE.red100,
    accent: DEFAULT_PALETTE.brandAccent50,
  },
  content: {
    primary: DEFAULT_PALETTE.grey900,
    secondary: DEFAULT_PALETTE.grey600,
    tertiary: DEFAULT_PALETTE.grey400,
  },
} as const satisfies TBrandingPalette;

export const createPalette = <
  TCustomPalette extends Readonly<Record<string, TPaletteColor>> = TEmptyObject,
>(
  customPalette?: TCustomPalette,
) => {
  return {...DEFAULT_PALETTE, ...customPalette} as [keyof TCustomPalette] extends [never]
    ? typeof DEFAULT_PALETTE
    : TMergeValues<typeof DEFAULT_PALETTE, TCustomPalette>;
};

export const createBrandingPalette = <
  TCustomPalette extends Readonly<Record<string, TPaletteColor>> = typeof DEFAULT_PALETTE,
>(
  customBranding?: TBrandingPalette<TCustomPalette>,
) => {
  return (customBranding ?? DEFAULT_BRANDING) as TBrandingPalette<TCustomPalette>;
};
